import React, { useRef, useState } from 'react';
import { BarChart as ReBarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList } from 'recharts';
import { Card, Modal, Button, Dropdown, Table } from 'antd';
import { EllipsisOutlined, EyeOutlined, DownloadOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';

const CustomBarChart = ({ labels, data, totalRecords, title, xAxisTitle, yAxisTitle, valueFormatter, bar1Label = 'totalValue', bar2Label = 'totalRecords', showBar2 = false }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isTableOpen, setIsTableOpen] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const chartRef = useRef(null);
  // Nuevo ref para el contenedor offscreen
  const captureRef = useRef(null);

  // Construir los datos
  let chartData = labels.map((label, index) => ({
    name: label,
    [bar1Label]: data[index],
    [bar2Label]: totalRecords[index],
  }));

  // Filtrar filas sin nombre y sin datos relevantes
  chartData = chartData.filter(row => {
    const sinNombre = !row.name || row.name === 'Sin nombre';
    const sinDatos = (!row[bar1Label] && !row[bar2Label]);
    return !(sinNombre && sinDatos);
  });

  // Ordenar por bar1Label descendente para top N
  const sortedChartData = [...chartData].sort((a, b) => (b[bar1Label] || 0) - (a[bar1Label] || 0));
  const N = 12;
  const useHorizontal = chartData.length > 20;
  let displayData = chartData;
  if (!showAll && chartData.length > N) {
    displayData = sortedChartData.slice(0, N);
  } else if (showAll) {
    displayData = sortedChartData;
  }

  // Función para formatear valores
  const formatValue = valueFormatter || ((v) => v);

  // Descargar la gráfica como imagen (usando html2canvas)
  const downloadChart = async () => {
    // Renderizar el contenedor offscreen y capturarlo
    if (captureRef.current) {
      const html2canvas = (await import('html2canvas')).default;
      // Ya no es necesario mostrar/ocultar el contenedor, solo capturarlo
      await new Promise((resolve) => setTimeout(resolve, 100));
      html2canvas(captureRef.current, { backgroundColor: '#fff', useCORS: true, scale: 2 }).then(canvas => {
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = `${title}.png`;
        link.click();
      });
    }
  };

  // Controlar si se muestra la segunda barra y la leyenda
  const mostrarBarra2 = showBar2 && bar2Label && bar2Label !== '' && totalRecords && totalRecords.some(val => val !== undefined && val !== null);

  // Menú de opciones (Ver y Descargar)
  const menuItems = [
    {
      key: 'view',
      label: (
        <span onClick={() => setIsModalOpen(true)}>
          <EyeOutlined style={{ fontSize: '18px', marginRight: '8px' }} />
          Ver Gráfica
        </span>
      ),
    },
    {
      key: 'download',
      label: (
        <span onClick={downloadChart}>
          <DownloadOutlined style={{ fontSize: '18px', marginRight: '8px' }} />
          Descargar
        </span>
      ),
    },
    {
      key: 'table',
      label: (
        <span onClick={() => setIsTableOpen(true)}>
          <EyeOutlined style={{ fontSize: '18px', marginRight: '8px' }} />
          Ver Tabla
        </span>
      ),
    },
  ];

  // Calcular el ángulo y tamaño de fuente óptimos para las etiquetas del eje X
  const maxLabelLength = displayData.reduce((max, l) => Math.max(max, (l.name || '').length), 0);
  let xTickAngle = -30;
  let xTickFontSize = 13;
  let xTickDy = 10;
  let xTickDx = -5;
  let xTickHeight = 50;
  // Ajuste más robusto para etiquetas largas y muchas etiquetas
  if (maxLabelLength > 20) {
    xTickAngle = -75;
    xTickFontSize = 10;
    xTickDy = 22;
    xTickDx = -10;
    xTickHeight = 110;
  } else if (maxLabelLength > 12) {
    xTickAngle = -60;
    xTickFontSize = 11;
    xTickDy = 18;
    xTickDx = -8;
    xTickHeight = 80;
  } else if (maxLabelLength > 7) {
    xTickAngle = -45;
    xTickFontSize = 12;
    xTickDy = 14;
    xTickDx = -6;
    xTickHeight = 65;
  }

  // Calcular el ancho mínimo dinámico de la gráfica
  const minBarWidth = Math.max(80, Math.min(120, maxLabelLength * 10 + 40)); // máximo 120px por barra
  const minChartWidth = Math.max(400, Math.min(1200, displayData.length * minBarWidth)); // máximo 1200px de ancho total

  // Tooltip personalizado para mostrar ambos valores aunque solo se muestre una barra
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const row = payload[0].payload;
      return (
        <div style={{ background: '#fff', border: '1px solid #ccc', padding: 10 }}>
          <div style={{ fontWeight: 'bold', marginBottom: 4 }}>{label}</div>
          <div style={{ color: '#0B80C4' }}>{bar1Label}: {formatValue(row[bar1Label])}</div>
          {row[bar2Label] !== undefined && (
            <div style={{ color: '#4CAF50' }}>{bar2Label}: {formatValue(row[bar2Label])}</div>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      {/* Contenedor offscreen para la descarga de la gráfica completa */}
      <div
        ref={captureRef}
        style={{
          position: 'fixed',
          left: '-99999px',
          top: 0,
          width: useHorizontal ? '1200px' : `${minChartWidth + 100}px`,
          height: useHorizontal ? `${displayData.length * 40 + 180}px` : '600px',
          background: '#fff',
          zIndex: -1,
          padding: 24,
          boxSizing: 'border-box',
          borderRadius: 20,
          // display: 'none', // Eliminado para que el SVG se renderice
        }}
      >
        {/* Título */}
        <h3 style={{
          color: '#0B3360',
          textAlign: 'center',
          fontWeight: 'bold',
          marginBottom: 0,
          marginTop: 0,
          fontSize: 28
        }}>{title}</h3>
        <div style={{ width: '100%', height: useHorizontal ? `${displayData.length * 40 + 100}px` : '500px', marginTop: 24 }}>
          <ResponsiveContainer width="100%" height={useHorizontal ? displayData.length * 40 + 100 : 500}>
            <ReBarChart
              data={displayData}
              margin={{ top: 20, right: 30, left: 50, bottom: xTickHeight + 40 }}
              barCategoryGap={8}
              layout={useHorizontal ? 'vertical' : 'horizontal'}
            >
              <CartesianGrid strokeDasharray="3 3" />
              {useHorizontal ? (
                <XAxis type="number" label={{ value: xAxisTitle, position: 'insideBottom', offset: -90, fontWeight: 'bold', fontSize: 15 }} tick={{ fontSize: xTickFontSize }} tickFormatter={formatValue} />
              ) : (
                <XAxis
                  dataKey="name"
                  label={{ value: xAxisTitle, position: 'insideBottom', offset: -90, fontWeight: 'bold', fontSize: 15 }}
                  textAnchor="end"
                  interval={0}
                  tick={{ fontSize: xTickFontSize, angle: -20, dy: xTickDy, dx: xTickDx, wordBreak: 'break-all', whiteSpace: 'pre-line', maxWidth: 200 }}
                  height={xTickHeight}
                />
              )}
              {useHorizontal ? (
                <YAxis type="category" dataKey="name" label={{ value: yAxisTitle, angle: -90, position: 'insideLeft', offset: 0, fontWeight: 'bold', fontSize: 15, dy: 200 }} width={200} />
              ) : (
                <YAxis label={{ value: yAxisTitle, angle: -90, position: 'insideLeft', offset: -10, fontWeight: 'bold', fontSize: 15, dy: 100 }} domain={[0, Math.max(...data.concat(totalRecords || []))]} tickFormatter={formatValue} />
              )}
              <Tooltip content={CustomTooltip} />
              {mostrarBarra2 && (
                <Legend
                  layout="vertical"
                  align="right"
                  verticalAlign="middle"
                  wrapperStyle={{ padding: 20 }}
                  payload={[
                    { value: bar1Label, type: 'rect', color: '#0B80C4' },
                    { value: bar2Label, type: 'rect', color: '#4CAF50' }
                  ]}
                />
              )}
              <Bar dataKey={bar1Label} fill="#0B80C4" barSize={20} name={bar1Label}>
                <LabelList dataKey={bar1Label} position="top" formatter={formatValue} angle={0} fill="black" />
              </Bar>
              {mostrarBarra2 && (
                <Bar dataKey={bar2Label} fill="#4CAF50" barSize={20} name={bar2Label}>
                  <LabelList dataKey={bar2Label} position="top" formatter={formatValue} angle={0} fill="black" />
                </Bar>
              )}
            </ReBarChart>
          </ResponsiveContainer>
        </div>
      </div>
      {/* Card visual principal */}
      <Card
        style={{
          position: 'relative',
          borderRadius: 20,
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          backdropFilter: 'blur(10px)',
          paddingTop: '30px',
          marginTop: '20px',
          width: '100%',
          height: '500px',
          minHeight: '500px',
          maxHeight: '500px',
          overflow: 'hidden',
        }}
      >
        {/* Título de la gráfica */}
        <h3 style={{
          color: '#0B3360',
          textAlign: 'center',
          fontWeight: 'bold',
          marginBottom: 0,
          marginTop: 0,
          fontSize: 20
        }}>{title}</h3>
        {/* Botón de menú con tres puntos */}
        <div style={{ position: 'absolute', top: 15, right: 15 }}>
          <Dropdown menu={{ items: menuItems }} trigger={['click']}>
            <Button shape="circle" icon={<EllipsisOutlined style={{ fontSize: '20px' }} />} />
          </Dropdown>
        </div>
        {/* Botón para alternar entre Top N y Ver todas */}
        {chartData.length > N && (
          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
            <Button size="small" onClick={() => setShowAll((v) => !v)}>
              {showAll ? `Mostrar Top ${N}` : `Ver todas (${chartData.length})`}
            </Button>
          </div>
        )}
        <div ref={chartRef} style={{ width: '100%', height: '420px', overflowX: 'auto', overflowY: 'auto' }}>
          <div style={{ minWidth: useHorizontal ? '100%' : `${minChartWidth}px`, height: useHorizontal ? `${displayData.length * 40 + 100}px` : '400px' }}>
            <ResponsiveContainer width="100%" height={useHorizontal ? displayData.length * 40 + 100 : 420}>
              <ReBarChart
                data={displayData}
                margin={{ top: 20, right: 10, left: 50, bottom: xTickHeight + 30 }}
                barCategoryGap={5}
                layout={useHorizontal ? 'vertical' : 'horizontal'}
              >
                <CartesianGrid strokeDasharray="3 3" />
                {useHorizontal ? (
                  <XAxis type="number" label={{ value: xAxisTitle, position: 'insideBottom', offset: -90, fontWeight: 'bold', fontSize: 15 }} tick={{ fontSize: xTickFontSize }} tickFormatter={formatValue} />
                ) : (
                  <XAxis
                    dataKey="name"
                    label={{ value: xAxisTitle, position: 'insideBottom', offset: -90, fontWeight: 'bold', fontSize: 15 }}
                    textAnchor="end"
                    interval={0}
                    tick={{ fontSize: xTickFontSize, angle: -20, dy: xTickDy, dx: xTickDx, wordBreak: 'break-all', whiteSpace: 'pre-line', maxWidth: 200 }}
                    height={xTickHeight}
                  />
                )}
                {useHorizontal ? (
                  <YAxis type="category" dataKey="name" label={{ value: yAxisTitle, angle: -90, position: 'insideLeft', offset: -10, fontWeight: 'bold', fontSize: 15 }} width={200} />
                ) : (
                  <YAxis label={{ value: yAxisTitle, angle: -90, position: 'insideLeft', offset: -20, fontWeight: 'bold', dy: 50, fontSize: 15 }} domain={[0, Math.max(...data.concat(totalRecords || []))]} tickFormatter={formatValue} />
                )}
                <Tooltip content={CustomTooltip} />
                {mostrarBarra2 && (
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    wrapperStyle={{ padding: 20 }}
                    payload={[
                      { value: bar1Label, type: 'rect', color: '#0B80C4' },
                      { value: bar2Label, type: 'rect', color: '#4CAF50' }
                    ]}
                  />
                )}
                <Bar dataKey={bar1Label} fill="#0B80C4" barSize={40} name={bar1Label}>
                  <LabelList dataKey={bar1Label} position="top" formatter={formatValue} angle={0} fill="black" />
                </Bar>
                {mostrarBarra2 && (
                  <Bar dataKey={bar2Label} fill="#4CAF50" barSize={40} name={bar2Label}>
                    <LabelList dataKey={bar2Label} position="top" formatter={formatValue} angle={0} fill="black" />
                  </Bar>
                )}
              </ReBarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {/* Modal con la gráfica ampliada */}
        <Modal
          title={title}
          open={isModalOpen}
          onCancel={() => setIsModalOpen(false)}
          footer={null}
          width={Math.min(Math.max(minChartWidth + 100, 1000), 1800)}
          style={{ maxWidth: '95vw' }}
        >
          {/* Botón para alternar entre Top N y Ver todas en el modal */}
          {chartData.length > N && (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <Button size="small" onClick={() => setShowAll((v) => !v)}>
                {showAll ? `Mostrar Top ${N}` : `Ver todas (${chartData.length})`}
              </Button>
            </div>
          )}
          <div style={{ width: '100%', height: '500px', display: 'flex', justifyContent: 'center', overflowX: 'auto', overflowY: 'auto' }}>
            <div style={{ minWidth: useHorizontal ? '100%' : `${minChartWidth}px`, height: useHorizontal ? `${displayData.length * 40 + 100}px` : '500px' }}>
              <ResponsiveContainer width="100%" height={useHorizontal ? displayData.length * 40 + 100 : 500}>
                <ReBarChart
                  data={displayData}
                  margin={{ top: 20, right: 30, left: 20, bottom: xTickHeight + 40 }}
                  barCategoryGap={8}
                  layout={useHorizontal ? 'vertical' : 'horizontal'}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  {useHorizontal ? (
                    <XAxis type="number" label={{ value: xAxisTitle, position: 'insideBottom', offset: -90, fontWeight: 'bold', fontSize: 15 }} tick={{ fontSize: xTickFontSize }} tickFormatter={formatValue} />
                  ) : (
                    <XAxis
                      dataKey="name"
                      label={{ value: xAxisTitle, position: 'insideBottom', offset: -90, fontWeight: 'bold', fontSize: 15 }}
                      textAnchor="end"
                      interval={0}
                      tick={{ fontSize: xTickFontSize, angle: -20, dy: xTickDy, dx: xTickDx, wordBreak: 'break-all', whiteSpace: 'pre-line', maxWidth: 200 }}
                      height={xTickHeight}
                    />
                  )}
                  {useHorizontal ? (
                    <YAxis type="category" dataKey="name" label={{ value: yAxisTitle, angle: -90, position: 'insideLeft', offset: 0, fontWeight: 'bold', fontSize: 15, dy: 200 }} width={200} />
                  ) : (
                    <YAxis label={{ value: yAxisTitle, angle: -90, position: 'insideLeft', offset: -10, fontWeight: 'bold', fontSize: 15, dy: 100 }} domain={[0, Math.max(...data.concat(totalRecords || []))]} tickFormatter={formatValue} />
                  )}
                  <Tooltip content={CustomTooltip} />
                  {mostrarBarra2 && (
                    <Legend
                      layout="vertical"
                      align="right"
                      verticalAlign="middle"
                      wrapperStyle={{ padding: 20 }}
                      payload={[
                        { value: bar1Label, type: 'rect', color: '#0B80C4' },
                        { value: bar2Label, type: 'rect', color: '#4CAF50' }
                      ]}
                    />
                  )}
                  <Bar dataKey={bar1Label} fill="#0B80C4" barSize={20} name={bar1Label}>
                    <LabelList dataKey={bar1Label} position="top" formatter={formatValue} angle={0} fill="black" />
                  </Bar>
                  {mostrarBarra2 && (
                    <Bar dataKey={bar2Label} fill="#4CAF50" barSize={20} name={bar2Label}>
                      <LabelList dataKey={bar2Label} position="top" formatter={formatValue} angle={0} fill="black" />
                    </Bar>
                  )}
                </ReBarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </Modal>
        {/* Modal con la tabla de datos */}
        <Modal
          title={null}
          open={isTableOpen}
          onCancel={() => setIsTableOpen(false)}
          footer={null}
          width={700}
          styles={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
        >
          {/* Título centrado */}
          <h3 style={{ width: '100%', textAlign: 'center', color: '#0B3360', fontWeight: 'bold', margin: 0, marginBottom: 16 }}>{`Datos de la gráfica: ${title}`}</h3>
          {/* Botón alineado a la derecha */}
          <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
            <Button
              icon={<DownloadOutlined />}
              type="primary"
              onClick={() => {
                // Construir los datos para Excel
                const columns = [
                  { title: xAxisTitle || 'Categoría', dataIndex: 'name', key: 'name' },
                  { title: bar1Label, dataIndex: bar1Label, key: bar1Label },
                ];
                // Siempre incluir la segunda columna si existe bar2Label y hay datos
                if (bar2Label && bar2Label !== '' && totalRecords && totalRecords.some(val => val !== undefined && val !== null)) {
                  columns.push({ title: bar2Label, dataIndex: bar2Label, key: bar2Label });
                }
                const dataExcel = chartData.map((row) => {
                  const obj = { [columns[0].title]: row.name, [columns[1].title]: row[bar1Label] };
                  if (columns.length > 2) obj[bar2Label] = row[bar2Label];
                  return obj;
                });
                const ws = XLSX.utils.json_to_sheet(dataExcel);
                const wb = XLSX.utils.book_new();
                let sheetName = title.replace(/[^a-zA-Z0-9_\- ]/g, '').slice(0, 31);
                if (!sheetName) sheetName = 'Datos';
                XLSX.utils.book_append_sheet(wb, ws, sheetName);
                XLSX.writeFile(wb, `${title.replace(/\s+/g, '_').toLowerCase()}.xlsx`);
              }}
            >
              Descargar Excel
            </Button>
          </div>
          <Table
            columns={[
              { title: xAxisTitle || 'Categoría', dataIndex: 'name', key: 'name' },
              { title: bar1Label, dataIndex: bar1Label, key: bar1Label, render: formatValue },
              ...(bar2Label && bar2Label !== '' && totalRecords && totalRecords.some(val => val !== undefined && val !== null)
                ? [{ title: bar2Label, dataIndex: bar2Label, key: bar2Label, render: formatValue }]
                : []),
            ]}
            dataSource={chartData.map((row, i) => ({ ...row, key: i }))}
            pagination={false}
            bordered
            size="small"
            style={{ width: '100%' }}
          />
        </Modal>
      </Card>
    </>
  );
};

export default CustomBarChart;
